import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Paginator } from 'primereact/paginator';
import { Dialog } from '../components/dialogs/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { useApi } from '../providers/api-provider';
import { useAuth } from '../providers/auth-provider';
import { useBookmarkSelection } from '../general/bookmark-selection';
import { AdminTask, AdminTasksDto, adminTaskLink, refreshAdminTaskCount } from '../general/admin-tasks';
import Config from '../general/config';
import '../assets/admin-tasks.css';

const categories = [{ value: 'all', label: 'All tasks' }, { value: 'payments', label: 'Payment exceptions' },
    { value: 'refunds', label: 'Refunds awaiting approval' }, { value: 'reviews', label: 'Reviews awaiting approval' }];

export default function AdminTasksPage() {
    const api = useApi(), identity = useAuth().getUser()?.sub;
    const [categoryKey, setCategory] = useBookmarkSelection('category');
    const [environmentKey, setEnvironment] = useBookmarkSelection('environment');
    const [pageKey, setPage] = useBookmarkSelection('page');
    const [sizeKey, setSize] = useBookmarkSelection('page_size');
    const [viewKey, setView] = useBookmarkSelection('view');
    const view = viewKey === 'dismissed' ? 'dismissed' : 'active';
    const category = categories.some(item => item.value === categoryKey) ? categoryKey! : 'all';
    const environment = ['production', 'sandbox'].includes(environmentKey ?? '') ? environmentKey! : Config.env === 'local' ? 'sandbox' : 'production';
    const page = /^[1-9][0-9]{0,5}$/.test(pageKey ?? '') ? pageKey! : '1';
    const size = ['10', '25', '50'].includes(sizeKey ?? '') ? sizeKey! : '10';
    const requestKey = JSON.stringify([identity, category, environment, page, size, view]);
    const [snapshot, setSnapshot] = useState<{ key: string; data: AdminTasksDto } | null>(null);
    const [error, setError] = useState('');
    const [revision, setRevision] = useState(0);
    const [dismissTask, setDismissTask] = useState<AdminTask | null>(null);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [actionError, setActionError] = useState('');
    const [notice, setNotice] = useState('');
    const actionPending = useRef(false);
    const currentIdentity = useRef(identity);
    currentIdentity.current = identity;
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => { setDismissTask(null); setNote(''); setNotice(''); setActionError(''); }, [identity]);
    const data = snapshot?.key === requestKey ? snapshot.data : null;
    useEffect(() => {
        if (!api || !identity) return;
        const controller = new AbortController();
        setSnapshot(null); setError('');
        api.getAdminTasks({ category, environment, page, page_size: size, view }, controller.signal)
            .then(result => { if (!controller.signal.aborted) setSnapshot({ key: requestKey, data: result }); })
            .catch(() => { if (!controller.signal.aborted) setError('Could not load admin tasks. Please try again.'); });
        return () => controller.abort();
    }, [api, identity, category, environment, page, size, view, requestKey, revision]);
    useEffect(() => {
        const refresh = () => setRevision(value => value + 1);
        window.addEventListener('focus', refresh);
        return () => window.removeEventListener('focus', refresh);
    }, []);
    const refresh = () => { setRevision(value => value + 1); refreshAdminTaskCount(); };
    const changeDismissal = async (task: AdminTask, restore: boolean) => {
        if (!api || actionPending.current) return;
        actionPending.current = true; setSaving(true); setActionError(''); setNotice('');
        try {
            await api.changeAdminTaskDismissal(restore ? 'restore' : 'dismiss', {
                item_key: task.item_key, fingerprint: task.fingerprint, environment, ...(restore ? {} : { note }),
            });
            if (mounted.current && currentIdentity.current === identity) {
                setDismissTask(null); setNote('');
                setNotice(restore ? 'Task restored to the active queue.' : task.category === 'reviews' ? 'Review declined. It remains unpublished.' : 'Task dismissed for all administrators.');
                refresh();
            }
        } catch (failure: any) {
            if (mounted.current && currentIdentity.current === identity)
                setActionError(failure?.response?.data?.message || 'Could not update this task. Please try again.');
        } finally {
            actionPending.current = false;
            if (mounted.current) setSaving(false);
        }
    };
    return <section className="admin-tasks" aria-labelledby="admin-tasks-title">
        <header className="admin-toolbar admin-tasks-header">
            <h1 id="admin-tasks-title" className="page-title">Admin tasks</h1>
            <Button label="Refresh" icon="pi pi-refresh" text disabled={saving} onClick={refresh} />
        </header>
        <p className="admin-tasks-intro">Tasks appear automatically. Dismiss an issue after reviewing it, or decline a review you do not want to publish. Dismissals are shared by all administrators; changed issues reappear.</p>
        <div className="admin-toolbar admin-tasks-filters">
            <label htmlFor="task-view">Status<Dropdown inputId="task-view" value={view} disabled={saving}
                options={[{ value: 'active', label: 'Active' }, { value: 'dismissed', label: 'Dismissed' }]}
                onChange={event => { setView(event.value === 'active' ? null : event.value); setPage(null, true); setActionError(''); }} /></label>
            <label htmlFor="task-category">Show<Dropdown inputId="task-category" value={category}
                disabled={saving}
                options={categories.map(item => ({ ...item, label: `${item.label}${data ? ` (${item.value === 'all' ? data.total : data.counts[item.value as keyof typeof data.counts]})` : ''}` }))}
                onChange={event => { setCategory(event.value === 'all' ? null : event.value); setPage(null, true); }} /></label>
            <label htmlFor="task-environment">Payments<Dropdown inputId="task-environment" value={environment}
                disabled={saving}
                options={[{ value: 'production', label: 'Live payments' }, { value: 'sandbox', label: 'Sandbox payments' }]}
                onChange={event => { setEnvironment(event.value); setPage(null, true); }} /></label>
        </div>
        {notice && <p role="status">{notice}</p>}
        {actionError && !dismissTask && <p role="alert">{actionError}</p>}
        {environment === 'sandbox' && <p role="status">Showing sandbox payment tasks. Reviews are the same in both environments.</p>}
        {error ? <div role="alert"><p>{error}</p><Button label="Retry" onClick={refresh} /></div>
            : !data ? <p role="status">Loading admin tasks…</p>
            : <>
                <div className="admin-task-list" aria-live="polite">
                    {data.data.length === 0 ? <p>{view === 'dismissed' ? 'No dismissed tasks' : 'No tasks need attention'}{category === 'all' ? '.' : ' in this category.'}</p>
                        : <ul>{data.data.map(task => <li key={task.item_key}>
                            <div className="admin-task-description"><h2>{view === 'dismissed' && task.category === 'reviews' ? 'Declined review' : task.kind}</h2><strong>{task.description}</strong><p>{task.reason}</p>
                                <small><time dateTime={task.recorded_at}>{new Date(task.recorded_at).toLocaleString()}</time>
                                    {' · '}{task.status}{task.error_code && ` · ${task.error_code}`}</small>
                                {task.reference && <small className="admin-task-reference">Reference: {task.reference}</small>}
                                {task.dismissed_at && <small className="admin-task-reference">Dismissed by {task.dismissed_by || 'an administrator'} on {new Date(task.dismissed_at).toLocaleString()}</small>}
                                {task.dismissal_note && <p className="admin-task-note">{task.dismissal_note}</p>}
                            </div>
                            <div className="admin-task-actions admin-toolbar">
                            <Link className="p-button p-button-text admin-task-link" to={adminTaskLink(task, environment)}>
                                {task.category === 'reviews' ? 'Review feedback' : task.purchase_key ? 'View transaction' : 'View Finance attention'}
                                <i className="pi pi-arrow-right" aria-hidden="true" />
                            </Link>
                            {view === 'dismissed'
                                ? <Button label="Restore" icon="pi pi-undo" text disabled={saving} onClick={() => void changeDismissal(task, true)} />
                                : <Button label={task.category === 'reviews' ? 'Decline review' : 'Dismiss'} icon="pi pi-times" text disabled={saving}
                                    onClick={() => { setDismissTask(task); setNote(''); setActionError(''); }} />}
                            </div>
                        </li>)}</ul>}
                </div>
                <Paginator first={(data.pager.page - 1) * data.pager.page_size} rows={data.pager.page_size} totalRecords={data.pager.total}
                    rowsPerPageOptions={[10, 25, 50]} currentPageReportTemplate="{totalRecords} tasks"
                    template="RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    onPageChange={event => { if (!saving) { setSize(String(event.rows)); setPage(event.rows !== Number(size) ? null : String(event.page + 1), true); } }} />
            </>}
        <Dialog visible={!!dismissTask} header={dismissTask?.category === 'reviews' ? 'Decline review' : 'Dismiss task'} modal
            className="admin-task-dismiss-dialog" style={{ width: '30rem', maxWidth: 'calc(100vw - 2rem)' }}
            closable={!saving} closeOnEscape={!saving} onHide={() => { if (!saving) setDismissTask(null); }}
            footer={<div className="admin-toolbar justify-content-end">
                <Button label="Cancel" text disabled={saving} onClick={() => setDismissTask(null)} />
                <Button label={dismissTask?.category === 'reviews' ? 'Decline review' : 'Dismiss'} icon="pi pi-check" loading={saving} disabled={saving}
                    onClick={() => { if (dismissTask) void changeDismissal(dismissTask, false); }} />
            </div>}>
            <p>{dismissTask?.description}</p>
            <p>{dismissTask?.category === 'reviews' ? 'This review will remain unpublished. You can restore it from Dismissed.' : 'This removes the issue from the active queue for all administrators. You can restore it from Dismissed.'}</p>
            <label htmlFor="task-dismiss-note">Internal note (optional)</label>
            <InputTextarea id="task-dismiss-note" value={note} maxLength={2000} rows={3} disabled={saving}
                className="w-full mt-2" onChange={event => setNote(event.target.value)} autoFocus />
            {actionError && <p role="alert">{actionError}</p>}
        </Dialog>
    </section>;
}
